import { DAYPARTING_TAB } from '@/components/hourlyBudget/daypartingTab';
import { isAutomationRuleEntity } from '@/components/automation/automationTypes';

/** Query-param identity for Automations nested sections. */

export const AUTOMATIONS_TAB_RULES = 'rules';
export const AUTOMATIONS_TAB_PENDING = 'pending';
export const AUTOMATIONS_TAB_EXECUTED = 'executed';
export const AUTOMATIONS_TAB_BID_SNAPSHOTS = 'bid-snapshots';
export const AUTOMATIONS_TAB_HOURLY_BUDGET = DAYPARTING_TAB;
export const AUTOMATIONS_TAB_SMART_BUDGET = 'smart-budget';

export const AUTOMATIONS_SECTION_BID_SNAPSHOTS = 'bid-snapshots';
export const AUTOMATIONS_SECTION_DAYPARTING = AUTOMATIONS_TAB_HOURLY_BUDGET;
export const AUTOMATIONS_SECTION_SMART_BUDGET = AUTOMATIONS_TAB_SMART_BUDGET;

export type AutomationsNavState = {
  tab: string;
  entity: string;
  rule: string;
  section: string;
};

export type AutomationsNavPatch = {
  tab: string;
  /** Pass `''` to clear. Omit to keep the current value (subject to tab rules). */
  entity?: string;
  rule?: string;
  section?: string;
};

type SearchParamsLike = {
  get: (key: string) => string | null;
};

export function parseAutomationsNav(searchParams: SearchParamsLike): AutomationsNavState {
  return {
    tab: searchParams.get('tab') || AUTOMATIONS_TAB_RULES,
    entity: searchParams.get('entity') || '',
    rule: searchParams.get('rule') || '',
    section: searchParams.get('section') || '',
  };
}

export function isBidSnapshotRulesView(state: Pick<AutomationsNavState, 'tab' | 'section'>): boolean {
  return (
    state.tab === AUTOMATIONS_TAB_BID_SNAPSHOTS ||
    state.section === AUTOMATIONS_SECTION_BID_SNAPSHOTS
  );
}

export function isDaypartingView(state: Pick<AutomationsNavState, 'tab' | 'section'>): boolean {
  return (
    state.tab === AUTOMATIONS_TAB_HOURLY_BUDGET ||
    state.section === AUTOMATIONS_SECTION_DAYPARTING
  );
}

export function isSmartBudgetView(state: Pick<AutomationsNavState, 'tab' | 'section'>): boolean {
  return (
    state.tab === AUTOMATIONS_TAB_SMART_BUDGET ||
    state.section === AUTOMATIONS_SECTION_SMART_BUDGET
  );
}

export function isRulesTabActive(tab: string): boolean {
  return (
    tab === AUTOMATIONS_TAB_RULES ||
    tab === AUTOMATIONS_TAB_BID_SNAPSHOTS ||
    tab === AUTOMATIONS_TAB_HOURLY_BUDGET ||
    tab === AUTOMATIONS_TAB_SMART_BUDGET
  );
}

function inBidSnapshotContext(current: AutomationsNavState, next: AutomationsNavPatch): boolean {
  const section = next.section !== undefined ? next.section : current.section;
  return (
    current.tab === AUTOMATIONS_TAB_BID_SNAPSHOTS ||
    current.section === AUTOMATIONS_SECTION_BID_SNAPSHOTS ||
    next.tab === AUTOMATIONS_TAB_BID_SNAPSHOTS ||
    section === AUTOMATIONS_SECTION_BID_SNAPSHOTS
  );
}

function inDaypartingContext(current: AutomationsNavState, next: AutomationsNavPatch): boolean {
  const section = next.section !== undefined ? next.section : current.section;
  return (
    current.tab === AUTOMATIONS_TAB_HOURLY_BUDGET ||
    current.section === AUTOMATIONS_SECTION_DAYPARTING ||
    next.tab === AUTOMATIONS_TAB_HOURLY_BUDGET ||
    section === AUTOMATIONS_SECTION_DAYPARTING
  );
}

function inSmartBudgetContext(current: AutomationsNavState, next: AutomationsNavPatch): boolean {
  const section = next.section !== undefined ? next.section : current.section;
  return (
    current.tab === AUTOMATIONS_TAB_SMART_BUDGET ||
    current.section === AUTOMATIONS_SECTION_SMART_BUDGET ||
    next.tab === AUTOMATIONS_TAB_SMART_BUDGET ||
    section === AUTOMATIONS_SECTION_SMART_BUDGET
  );
}

/**
 * Resolve Automations search params.
 *
 * Bid Snapshot Rules use `tab=bid-snapshots` and must not reuse
 * `tab=rules&entity=placement` (Placement Rules).
 * Dayparting uses `tab=hourly-budget` the same way: Rules under Dayparting
 * must stay on Dayparting, not Campaign Rules.
 */
export function buildAutomationsSearchParams(
  current: AutomationsNavState,
  next: AutomationsNavPatch
): Record<string, string> {
  let tab = next.tab;
  let entity = next.entity !== undefined ? next.entity : current.entity;
  let rule = next.rule !== undefined ? next.rule : current.rule;
  let section = next.section !== undefined ? next.section : current.section;

  const explicitEntity = isAutomationRuleEntity(next.entity);

  if (tab === AUTOMATIONS_TAB_RULES && !explicitEntity) {
    if (inBidSnapshotContext(current, next)) {
      tab = AUTOMATIONS_TAB_BID_SNAPSHOTS;
    } else if (inDaypartingContext(current, next)) {
      tab = AUTOMATIONS_TAB_HOURLY_BUDGET;
    } else if (inSmartBudgetContext(current, next)) {
      tab = AUTOMATIONS_TAB_SMART_BUDGET;
    }
  }

  if (tab === AUTOMATIONS_TAB_RULES && explicitEntity) {
    section = '';
  }

  if (tab === AUTOMATIONS_TAB_BID_SNAPSHOTS) {
    entity = '';
    section = AUTOMATIONS_SECTION_BID_SNAPSHOTS;
    rule = next.rule !== undefined ? next.rule : '';
  } else if (tab === AUTOMATIONS_TAB_HOURLY_BUDGET) {
    entity = '';
    section = AUTOMATIONS_SECTION_DAYPARTING;
    rule = next.rule !== undefined ? next.rule : '';
  } else if (tab === AUTOMATIONS_TAB_SMART_BUDGET) {
    entity = '';
    section = AUTOMATIONS_SECTION_SMART_BUDGET;
    rule = next.rule !== undefined ? next.rule : '';
  } else if (tab === AUTOMATIONS_TAB_PENDING || tab === AUTOMATIONS_TAB_EXECUTED) {
    if (
      current.tab === AUTOMATIONS_TAB_BID_SNAPSHOTS ||
      section === AUTOMATIONS_SECTION_BID_SNAPSHOTS
    ) {
      section = AUTOMATIONS_SECTION_BID_SNAPSHOTS;
      if (next.entity === undefined) entity = '';
    } else if (
      current.tab === AUTOMATIONS_TAB_HOURLY_BUDGET ||
      section === AUTOMATIONS_SECTION_DAYPARTING
    ) {
      section = AUTOMATIONS_SECTION_DAYPARTING;
      if (next.entity === undefined) entity = '';
    } else if (
      current.tab === AUTOMATIONS_TAB_SMART_BUDGET ||
      section === AUTOMATIONS_SECTION_SMART_BUDGET
    ) {
      section = AUTOMATIONS_SECTION_SMART_BUDGET;
      if (next.entity === undefined) entity = '';
    }
  }

  const params: Record<string, string> = { tab };
  if (entity) params.entity = entity;
  if (rule) params.rule = rule;
  if (
    section &&
    tab !== AUTOMATIONS_TAB_BID_SNAPSHOTS &&
    tab !== AUTOMATIONS_TAB_HOURLY_BUDGET &&
    tab !== AUTOMATIONS_TAB_SMART_BUDGET
  ) {
    params.section = section;
  }
  return params;
}
